import { getBundleId } from '@wix/editor-elements-conventions';

import {
  ILibraryManifestContext,
  IRegistryManifestContext,
} from '../registry.types';
import { isCSS, isJS, getExtension } from '../toolbox/assets';
import { createInternalException } from '../errors/RegistryError';
import { createLibraryManifestBuilder } from './builders/libraryManifestBuilder';
import { createHostManifestBuilder } from './builders/hostManifestBuilder';

export interface ITransitionModel {
  js?: string;
  css?: string;
  deps?: Array<string>;
}

function getDepsURLs(indexes: Array<number>, shared: Array<string> = []) {
  if (indexes.some(index => !shared[index])) {
    throw createInternalException('Wrong Manifest Structure').withDetails(
      'There is link to the shared chunk, but chunk does not exist',
    );
  }

  return indexes.map(index => shared[index]);
}

function extractURLs(
  urls: Array<string | Array<number>>,
  shared: Array<string> = [],
) {
  const model: ITransitionModel = {};

  urls.forEach(url => {
    if (Array.isArray(url)) {
      model.deps = getDepsURLs(url, shared);
    } else {
      const extension = getExtension(url);
      if (isJS(extension)) {
        model.js = url;
      } else if (isCSS(extension)) {
        model.css = url;
      }
    }
  });

  return model;
}

function addComponentModels(
  hostBuilder: ReturnType<typeof createHostManifestBuilder>,
  {
    componentName,
    model,
  }: {
    componentName: string;
    model: ITransitionModel & { id: string };
  },
) {
  const componentBuilder = hostBuilder.getComponentBuilder(componentName);

  if (model.js) {
    componentBuilder.addUrl(model.id, model.js);
  }

  if (model.css) {
    componentBuilder.addStyleAsset(model.css);
  }

  if (model.deps) {
    componentBuilder.addDeps(model.deps);
  }
}

function addBatchPart(
  hostBuilder: ReturnType<typeof createHostManifestBuilder>,
  {
    componentName,
    part,
    model,
  }: {
    componentName: string;
    part: string;
    model: ITransitionModel & { id: string };
  },
) {
  const componentBuilder = hostBuilder.getComponentBuilder(componentName);

  /**
   * remove all prev. added assets that leads to the default part bundle
   */
  componentBuilder.filterAssets(asset => asset.part !== part);

  if (model.js) {
    componentBuilder.addPart(model.id, part, model.js, true);
  }

  if (model.css) {
    componentBuilder.addStyleAsset(model.css, part);
  }

  if (model.deps) {
    componentBuilder.addDeps(model.deps);
  }
}

export function parseRegistryManifest({
  topology,
  manifest,
}: IRegistryManifestContext): ILibraryManifestContext {
  const baseURL = manifest.baseURL.endsWith('/')
    ? manifest.baseURL
    : `${manifest.baseURL}/`;

  const libraryBuilder = createLibraryManifestBuilder({
    topology,
  });

  const buildComponentId = (componentName: string, part?: string) => {
    return getBundleId({
      componentName,
      part,
      namespace: manifest.namespace,
      host: manifest.host,
    });
  };

  const buildBatchId = (batch: string) => {
    return getBundleId({
      batch,
      namespace: manifest.namespace,
      host: manifest.host,
    });
  };

  const withBaseUrl = (bundle: string) => {
    return `${baseURL}${bundle}`;
  };

  const buildURL = (id: string, hash?: string) => {
    if (!hash || !id) {
      return undefined;
    }

    return withBaseUrl(`${id}.${hash}`);
  };

  if (manifest.environment) {
    if (manifest.environment.runtime) {
      libraryBuilder.addWebpackRuntimeBundle(
        buildURL('webpack-runtime', manifest.environment.runtime) as string,
      );
    }

    if (manifest.environment.hot) {
      libraryBuilder.addHotBundle(
        buildURL('hot', manifest.environment.hot) as string,
      );
    }
  }

  const hostBuilder = libraryBuilder.getHostBuilder(manifest.host);

  if (manifest.statics) {
    const statics = manifest.statics;

    Object.keys(statics).forEach(componentName => {
      hostBuilder.addComponentStatics(componentName, {
        ...statics[componentName],
      });
    });
  }

  if (manifest.libraryStatics) {
    const libraryStatics = manifest.libraryStatics;

    libraryBuilder.addLibraryStatics(libraryStatics);
  }

  if (manifest.model) {
    const [modelHash, stylesHash] = manifest.model;
    const id = getBundleId({
      namespace: manifest.namespace,
      host: manifest.host,
    });

    const modelURL = buildURL(id, modelHash);
    const stylesURL = buildURL(id, stylesHash);

    if (modelURL) {
      hostBuilder.addUrl(id, modelURL);
    }

    if (stylesURL) {
      hostBuilder.addStyleAsset(stylesURL);
    }

    if (manifest.deps) {
      hostBuilder.addDeps(
        getDepsURLs(manifest.deps, manifest.shared).map(withBaseUrl),
      );
    }
  }

  if (manifest.assets) {
    const assets = manifest.assets;

    assets.forEach(asset => {
      const [type, url] = asset;

      if (url && type) {
        const assetsURL = `${baseURL}${url}`;

        const extension = getExtension(assetsURL);
        libraryBuilder.addLibraryAsset(assetsURL, type, extension);
      }
    });
  }

  if (manifest.components) {
    const components = manifest.components;

    Object.keys(components).forEach(componentName => {
      const model = extractURLs(components[componentName], manifest.shared);
      const id = buildComponentId(componentName);

      addComponentModels(hostBuilder, {
        componentName,
        model: {
          id,
          js: buildURL(id, model.js),
          css: buildURL(id, model.css),
          deps: model.deps?.map(withBaseUrl),
        },
      });
    });
  }

  if (manifest.parts) {
    const parts = manifest.parts;

    Object.keys(parts).forEach(componentName => {
      Object.keys(parts[componentName]).forEach(part => {
        const componentBuilder = hostBuilder.getComponentBuilder(componentName);

        const [modelHash, stylesHash] = parts[componentName][part];
        const id = buildComponentId(componentName, part);

        const modelURL = buildURL(id, modelHash);
        const stylesURL = buildURL(id, stylesHash);

        if (modelURL) {
          componentBuilder.addPart(id, part, modelURL);
        }

        if (stylesURL) {
          componentBuilder.addStyleAsset(stylesURL, part);
        }
      });
    });
  }

  if (manifest.batches) {
    const batches = manifest.batches;
    Object.entries(batches).forEach(([batchId, batch]) => {
      const v2 = batch.url_v2 && batch.url_v2.length;

      const id = buildBatchId(batchId);
      const id_v2 = v2
        ? getBundleId({
            part: batchId,
            namespace: manifest.namespace,
            host: manifest.host,
          })
        : id;

      const model = v2
        ? extractURLs(batch.url_v2 as any, manifest.shared)
        : extractURLs(batch.url, manifest.shared);

      const modelURL = buildURL(id_v2, model.js);
      const stylesURL = buildURL(id_v2, model.css);

      const addComponentBatchPart = ({
        componentName,
        part,
      }: {
        componentName: string;
        part: string;
      }) => {
        addBatchPart(hostBuilder, {
          componentName,
          part,
          model: {
            id,
            js: modelURL,
            css: stylesURL,
            deps: model.deps?.map(withBaseUrl),
          },
        });
      };

      if (batch.parts && batch.parts.length) {
        batch.parts.forEach(([part, ...components]) => {
          components.forEach(componentName => {
            addComponentBatchPart({
              componentName,
              part,
            });
          });
        });
      }

      batch.components.forEach(componentName => {
        addComponentModels(hostBuilder, {
          componentName,
          model: {
            id,
            js: buildURL(id_v2, model.js),
            css: buildURL(id_v2, model.css),
            deps: model.deps?.map(withBaseUrl),
          },
        });

        if (batch.parts && batch.parts.length) {
          batch.parts.forEach(([part]) => {
            addComponentBatchPart({
              componentName,
              part,
            });
          });
        }
      });
    });
  }

  return libraryBuilder.build();
}
